import React from 'react';
import {
  Hammer,
  ExternalLink,
  User,
  Users,
  KeyRound,
  Building2,
  MapPin,
  Globe,
  Search,
  Image,
  AlertCircle,
  LucideIcon,
} from 'lucide-react';

interface Tool {
  name: string;
  desc: string;
  url: string;
}

interface Category {
  title: string;
  blurb: string;
  icon: LucideIcon;
  tools: Tool[];
}

const categories: Category[] = [
  {
    title: 'People & Identity',
    blurb: 'Names, phone numbers, and addresses.',
    icon: User,
    tools: [
      { name: 'ThatsThem', desc: 'Free people, phone & address lookup', url: 'https://thatsthem.com' },
      { name: 'Pipl', desc: 'Deep identity search (paid)', url: 'https://pipl.com' },
      { name: 'PeekYou', desc: 'Aggregated web presence by name', url: 'https://www.peekyou.com' },
      { name: 'FastPeopleSearch', desc: 'US people & reverse phone lookup', url: 'https://www.fastpeoplesearch.com' },
    ],
  },
  {
    title: 'Social Media',
    blurb: 'Usernames, profiles, and networks.',
    icon: Users,
    tools: [
      { name: 'WhatsMyName', desc: 'Enumerate a username across 600+ sites', url: 'https://whatsmyname.app' },
      { name: 'Sherlock', desc: 'CLI username hunt across platforms', url: 'https://github.com/sherlock-project/sherlock' },
      { name: 'Social Searcher', desc: 'Real-time social media search', url: 'https://www.social-searcher.com' },
      { name: 'Instant Username', desc: 'Instant username availability check', url: 'https://instantusername.com' },
    ],
  },
  {
    title: 'Breaches & Credentials',
    blurb: 'Leaked emails, passwords, and data.',
    icon: KeyRound,
    tools: [
      { name: 'Have I Been Pwned', desc: 'Check emails against known breaches', url: 'https://haveibeenpwned.com' },
      { name: 'DeHashed', desc: 'Search breached credentials & data (paid)', url: 'https://dehashed.com' },
      { name: 'Intelligence X', desc: 'Search leaks, darkweb & pastes', url: 'https://intelx.io' },
      { name: 'LeakCheck', desc: 'Breach lookup by email/username', url: 'https://leakcheck.io' },
    ],
  },
  {
    title: 'Corporate & Financial',
    blurb: 'Companies, officers, and filings.',
    icon: Building2,
    tools: [
      { name: 'Companies House (UK)', desc: 'Official UK company & director records', url: 'https://find-and-update.company-information.service.gov.uk' },
      { name: 'OpenCorporates', desc: 'Cross-jurisdiction company database', url: 'https://opencorporates.com' },
      { name: 'SEC EDGAR', desc: 'US public-company filings', url: 'https://www.sec.gov/edgar' },
      { name: 'OCCRP Aleph', desc: 'Investigative data & leaks archive', url: 'https://aleph.occrp.org' },
    ],
  },
  {
    title: 'Location & Geospatial',
    blurb: 'Maps, imagery, and chronolocation.',
    icon: MapPin,
    tools: [
      { name: 'Google Earth', desc: 'Satellite imagery & historical views', url: 'https://earth.google.com/web' },
      { name: 'Yandex Maps', desc: 'Strong non-Western street coverage', url: 'https://yandex.com/maps' },
      { name: 'SunCalc', desc: 'Shadow & sun position for chronolocation', url: 'https://www.suncalc.org' },
      { name: 'Overpass Turbo', desc: 'Query OpenStreetMap features', url: 'https://overpass-turbo.eu' },
    ],
  },
  {
    title: 'Domains & Infrastructure',
    blurb: 'WHOIS, DNS, and exposed hosts.',
    icon: Globe,
    tools: [
      { name: 'Shodan', desc: 'Search internet-connected devices', url: 'https://www.shodan.io' },
      { name: 'crt.sh', desc: 'Certificate transparency / subdomains', url: 'https://crt.sh' },
      { name: 'DNSDumpster', desc: 'DNS recon & subdomain mapping', url: 'https://dnsdumpster.com' },
      { name: 'ViewDNS', desc: 'WHOIS, reverse IP & DNS tools', url: 'https://viewdns.info' },
    ],
  },
  {
    title: 'Images & Verification',
    blurb: 'Reverse search and metadata.',
    icon: Image,
    tools: [
      { name: 'Yandex Images', desc: 'Best-in-class reverse image search', url: 'https://yandex.com/images' },
      { name: 'TinEye', desc: 'Find where an image first appeared', url: 'https://tineye.com' },
      { name: 'Google Lens', desc: 'Visual match & object search', url: 'https://lens.google.com' },
      { name: 'Forensically', desc: 'Image error-level & metadata analysis', url: 'https://29a.ch/photo-forensics' },
    ],
  },
  {
    title: 'Search & Dorking',
    blurb: 'Advanced operators and archives.',
    icon: Search,
    tools: [
      { name: 'DorkSearch', desc: 'Build Google dorks with a UI', url: 'https://dorksearch.com' },
      { name: 'Google Advanced Search', desc: 'Guided operator search form', url: 'https://www.google.com/advanced_search' },
      { name: 'Wayback Machine', desc: 'Archived versions of web pages', url: 'https://web.archive.org' },
      { name: 'archive.today', desc: 'On-demand page snapshots', url: 'https://archive.ph' },
    ],
  },
];

const Tools: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 py-8 px-6">
      <div className="max-w-none">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white">
            <Hammer className="w-7 h-7" />
          </div>
          <div>
            <p className="text-xs font-bold tracking-widest text-orange-500 uppercase mb-1">
              The Toolkit
            </p>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">Tools &amp; Techniques</h1>
          </div>
        </div>
        <p className="text-gray-500 dark:text-slate-400 max-w-2xl mb-8">
          A curated directory of go-to resources, grouped by what you're trying to find.
          Pick an area and jump straight to the tool.
        </p>

        <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-amber-600 mb-8" />

        {/* Ethics note */}
        <div className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-500/10 p-5 mb-10">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 dark:text-slate-300">
              Use these only for authorized, lawful research. Some tools are paid or
              rate-limited, and terms of service vary by site.
            </p>
          </div>
        </div>

        {/* Category grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.title}
                className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col transition-all duration-300 hover:shadow-lg hover:border-orange-200"
              >
                {/* card header */}
                <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center text-white flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 leading-tight">{cat.title}</h3>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{cat.blurb}</p>
                  </div>
                </div>

                {/* tool links */}
                <div className="p-2 flex-1">
                  {cat.tools.map((tool) => (
                    <a
                      key={tool.name}
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-300 dark:text-slate-500 group-hover:text-orange-500 flex-shrink-0 mt-0.5 transition-colors" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100 group-hover:text-orange-600 transition-colors">
                          {tool.name}
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-slate-400">{tool.desc}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Tools;
